import { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    Button,
    Table,
    Badge,
    Modal,
    ModalHeader,
    ModalBody,
    Input,
    Spinner,
    Pagination,
    PaginationItem,
    PaginationLink,
    Alert,
    ModalFooter,
    Label,
    FormGroup
} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import {
    FaMoneyBillWave,
    FaPlus,
    FaEdit,
    FaSearch,
    FaCheckCircle,
    FaClock,
    FaTimes,
    FaFileInvoice,
    FaTruck,
    FaFilter,
    FaInfoCircle,
    FaList,
    FaTh,
    FaAngleDoubleLeft,
    FaAngleLeft,
    FaAngleRight,
    FaAngleDoubleRight,
    FaImage,
    FaBarcode,
    FaBox,
    FaTag,
    FaBoxes,
    FaLink,
    FaSync,
    FaTrashAlt
} from 'react-icons/fa';
import { reglementActions } from '../../sagas/reglementSlice';
import { formatPrice, roundToNearestInteger } from '../../helpers/utility';
import ReglementForm from './ReglementForm';
import { useLocation, useNavigate } from 'react-router-dom';
import { clientActions } from '../../sagas/clientSlice';
import { debounce, set } from 'lodash';
import { deliveryActions } from '../../sagas/deliveryNoteSlice';
import { invoiceActions } from '../../sagas/invoiceSlice';
import Select from "react-select";
import { productActions } from '../../sagas/productSlice';
import { config } from '@fortawesome/fontawesome-svg-core';
import pdfMake from "pdfmake/build/pdfmake";
import "pdfmake/build/vfs_fonts";
import { FaReceipt } from 'react-icons/fa';
import { soldeActions } from "../../sagas/soldeSlice";

const PaymentAllocation = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { loading, error, allocations } = useSelector(state => state.reglements);

    const now = new Date();
    const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    const pad = (n) => n.toString().padStart(2, '0');
    const formatDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

    const searchInputRef = useRef(null);

    const [filters, setFilters] = useState({
        dateFrom: formatDate(todayDate),
        dateTo: formatDate(tomorrowDate),
        query: '',
        mode:null,
    });

    useEffect(() => {
        // Auto focus on mount
        searchInputRef.current?.focus();

        // Add keyboard shortcut
        const handleKeyDown = (e) => {
            if (e.key === 'F2') {
                e.preventDefault();
                searchInputRef.current?.focus();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [allocations]);

    useEffect(() => {
        dispatch(reglementActions.allocationsRequest(filters));
    }, [filters.dateFrom, filters.dateTo, filters.mode]);

    const debouncedSearch = () => {
        dispatch(reglementActions.allocationsRequest({
            ...filters,
        }));
    }


    const clearFilters = () => {
        setFilters({
            query: '',
            mode:'',
            dateFrom: formatDate(todayDate),
            dateTo: formatDate(tomorrowDate)
        });
    };


    const getPaymentModeLabel = (mode) => {
        switch (mode) {
            case 'ESPECES': return t('facture.modeReglement.espece');
            case 'CHEQUE': return t('facture.modeReglement.cheque');
            case 'VIREMENT': return t('facture.modeReglement.virement');
            case 'CARTE_BANCAIRE': return t('facture.modeReglement.card');
            default: return mode;
        }
    };


    const navigate = useNavigate();

    return (
        <Container fluid>
            <Row className="mb-2">
                <Col>
                    <Card className="shadow-sm">
                        <CardBody className="d-flex justify-content-between align-items-center">
                            <h4 className="mb-0 d-flex align-items-center">
                                <FaMoneyBillWave className="text-primary me-2" />
                                {t('reglement.allocations')}
                            </h4>

                        </CardBody>
                    </Card>
                </Col>
            </Row>
            {/* Filters */}
            <Card className="shadow-sm">
                <CardBody>
                    <Row className="g-2 align-items-end">
                        <Col lg={3} md={3} sm={6} xs={12}>
                            <FormGroup className="mb-0">
                                <Label className="mb-1">{t('reglement.search')}</Label>
                                <div className="input-group">

                                    <Input
                                        type="text"
                                        innerRef={searchInputRef}
                                        placeholder={t('reglement.searchPlaceholder')}
                                        value={filters.query}
                                        onChange={e => {
                                            const value = e.target.value.toUpperCase();
                                            setFilters(prevFilters => ({
                                                ...prevFilters,
                                                query: value
                                            }));
                                        }}
                                        className="text-uppercase"
                                        style={{ textTransform: 'uppercase' }}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter') {
                                                debouncedSearch(e.target.value.toUpperCase());
                                            }
                                        }}
                                    />
                                </div>
                            </FormGroup>
                        </Col>
                        <Col md={3}>
                            <FormGroup>
                                <Label for="modeReglement">
                                    <FaMoneyBillWave className="me-1" />
                                    {t('facture.modeReglement')} *
                                </Label>
                                <Input
                                    type="select"
                                    id="mode"
                                    name="mode"
                                    value={filters.mode}
                                    onChange={(e) => setFilters(f => ({ ...f, mode: e.target.value }))}
                                >
                                    <option value="">{t('common.select')}</option>
                                    <option value="ESPECES">{t('facture.modeReglement.espece')}</option>
                                    <option value="CHEQUE">{t('facture.modeReglement.cheque')}</option>
                                    <option value="VIREMENT">{t('facture.modeReglement.virement')}</option>
                                    <option value="CARTE_BANCAIRE">{t('facture.modeReglement.card')}</option>
                                    <option value="TRAITE">{t('facture.modeReglement.traite')}</option>
                                </Input>
                            </FormGroup>
                        </Col>
                        <Col lg={2} md={2} sm={6} xs={12}>
                            <FormGroup className="mb-0">
                                <Label for="dateFrom" className="mb-1">Date début</Label>
                                <Input
                                    type="date"
                                    id="dateFrom"
                                    value={filters.dateFrom}
                                    onChange={e => setFilters(f => ({ ...f, dateFrom: e.target.value }))}
                                />
                            </FormGroup>
                        </Col>
                        <Col lg={2} md={2} sm={6} xs={12}>
                            <FormGroup className="mb-0">
                                <Label for="dateTo" className="mb-1">Date fin</Label>
                                <Input
                                    type="date"
                                    id="dateTo"
                                    value={filters.dateTo}
                                    onChange={e => setFilters(f => ({ ...f, dateTo: e.target.value }))}
                                />
                            </FormGroup>
                        </Col>
                        <Col lg={1} md={1} sm={6} xs={12} className="d-flex align-items-end">
                            <Button
                                color="secondary"
                                onClick={clearFilters}
                                title={t('common.clearFilters')}
                                className="mb-3"
                                style={{ minHeight: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                                <FaFilter />
                            </Button>
                        </Col>
                    </Row>
                </CardBody>
            </Card>
            <Card className="shadow-sm mt-4">
                <CardBody>
                    <Row>
                        <Col>

                            {loading ? (
                                <div className="text-center my-4"><Spinner color="primary" /></div>
                            ) : error ? (
                                <div className="text-danger">Erreur lors du chargement des réglements.</div>
                            ) : (
                                <Table striped bordered hover size="sm">
                                    <thead className="bg-light">
                                        <tr>
                                            <th>{t('payment.refDocument')}</th>
                                            <th>{t('payment.refPaiement')}</th>
                                            <th>{t('payment.date')}</th>
                                            <th>{t('payment.mode')}</th>
                                            <th>{t('payment.piece')}</th>
                                            <th>{t('payment.banque')}</th>
                                            <th>{t('payment.dateEcheance')}</th>
                                            <th>{t('payment.totalDue')}</th>
                                            <th>{t('payment.totalPaid')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {allocations.map((payment) => (
                                            <tr key={payment.id}>
                                                <td>{payment.numeroDocument || '-'}</td>
                                                <td>{payment.refReglement || '-'}</td>
                                                <td>{payment.createdAt}</td>
                                                <td>{getPaymentModeLabel(payment.modeReglement)}</td>
                                                <td>{payment.numeroCheque || payment.referencePaiement || '-'}</td>
                                                <td>{payment.banque || '-'}</td>
                                                <td>{payment.dateEcheance || '-'}</td>
                                                <td className="text-end fw-bold">{formatPrice(payment.montantDocument, false)}</td>
                                                <td className="text-end fw-bold">{formatPrice(payment.montantAlloue, false)}</td>

                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot className="bg-light">
                                        <tr>
                                            <td colSpan="7" className="text-end fw-bold">{t('payment.total')}:</td>
                                            <td className="text-end fw-bold">{formatPrice(allocations.reduce((sum, payment) => sum + (payment.montantDocument || 0), 0), false)}</td>
                                            <td className="text-end fw-bold">{formatPrice(allocations.reduce((sum, payment) => sum + (payment.montantAlloue || 0), 0), false)}</td>
                                        </tr>
                                    </tfoot>
                                </Table>
                            )}

                        </Col>
                    </Row>
                </CardBody>
            </Card>


        </Container >
    );
};

export default PaymentAllocation;


